import type { ReactNode } from 'react';
import { AppShell, type MenuItem } from '@tessma/design-system';
import { navigationFor } from './navigation.js';
import type { Session } from '../session/session.js';

/**
 * The frozen shell, wired to the router.
 *
 * `AppShell` is used exactly as the design system froze it. This component
 * decides which of the header affordances have an implementation behind them;
 * the ones that do not stay in the frozen v1.1 disabled treatment (OD-4 §2),
 * because they must not appear functional, must not call a placeholder API,
 * and must not be hidden either — hiding them would change the frozen layout
 * to make an implementation easier.
 *
 * THE RULE IS "INERT UNTIL A HANDLER EXISTS", not "inert until a stage says
 * so". Stage 7 made that true of Ask TESSMA AI. P1 makes it true of global
 * search, which has had a real route since Stage 6 and a header field that
 * could not reach it. Notifications stay inert: the `notification` table
 * exists, no endpoint serves it, and an unread count nobody can compute is
 * fabricated data rather than a design detail. P3 builds that endpoint.
 */
export interface ApplicationShellProps {
  readonly session: Session;
  readonly activeModuleId: string;
  readonly activePage: string;
  readonly onNavigate: (moduleId: string, page: string) => void;
  /** Opens the Ask TESSMA AI drawer. Absent leaves the button in the inert treatment. */
  readonly onAskAi?: () => void;
  /** Runs a global search. Absent leaves the field in the inert treatment. */
  readonly onSearch?: (query: string) => void;
  /** Navigates to a path the account menu offers. */
  readonly onOpenPath?: (path: string) => void;
  /** One line under the page title, from the route table. */
  readonly pageDescription?: string | undefined;
  /** The `<h1>`, when the frozen heading is not simply the page's name. */
  readonly pageTitle?: string | undefined;
  /**
   * The caller's own unread notifications, when a screen has been told one.
   *
   * ABSENT IS NOT ZERO. Omitted means nobody has asked the question and the
   * bell carries no badge; a zero means it was asked and the answer was none.
   * The shell already draws those two differently, and the distinction is the
   * whole reason the fabricated "3" was removed rather than replaced.
   */
  readonly notificationCount?: number | undefined;
  readonly children: ReactNode;
}

/**
 * What the account menu offers, and nothing else.
 *
 * EVERY ITEM IS A ROUTE THAT EXISTS, and each appears only where the session
 * holds the permission that route and the API both require — the same courtesy
 * the sidebar and the Finance landing tiles apply, and not the control: the
 * route guard refuses the URL typed by hand and the server refuses it again.
 *
 * There is deliberately no "Profile" and no "Preferences". Neither has a route,
 * and a menu item that opens nothing is the same lie as a count nobody counted.
 */
function accountMenuItems(
  session: Session,
  onOpenPath: (path: string) => void,
): readonly MenuItem[] {
  const items: MenuItem[] = [];
  if (session.permissions.includes('platform.tenant.read')) {
    items.push({
      id: 'settings',
      label: 'Settings',
      icon: 'gear-six',
      onSelect: () => {
        onOpenPath('/m/administration');
      },
    });
  }
  items.push({
    id: 'sign-out',
    label: 'Sign out',
    icon: 'arrow-square-out',
    separatorBefore: items.length > 0,
    onSelect: () => {
      onOpenPath('/logout');
    },
  });
  return items;
}

export function ApplicationShell({
  session,
  activeModuleId,
  activePage,
  onNavigate,
  onAskAi,
  onSearch,
  onOpenPath,
  pageDescription,
  pageTitle,
  notificationCount,
  children,
}: ApplicationShellProps) {
  /*
   * SEARCH GOES LIVE ONLY FOR SOMEBODY WHO MAY SEARCH. `/search` guards on
   * `platform.search.query` and the API checks it again, so a live field for a
   * session without it would lead to a refusal the reader could have been
   * spared. Without the permission it keeps the frozen disabled treatment,
   * which already says why in its accessible name.
   */
  const canSearch = onSearch !== undefined && session.permissions.includes('platform.search.query');
  const accountItems = onOpenPath === undefined ? [] : accountMenuItems(session, onOpenPath);

  return (
    <AppShell
      modules={navigationFor(session)}
      activeModuleId={activeModuleId}
      activePage={activePage}
      onNavigate={onNavigate}
      user={{
        name: session.user.displayName,
        role: session.user.roleLabel,
        initials: session.user.initials,
      }}
      inertAffordances
      {...(onAskAi === undefined ? {} : { onAskAi })}
      {...(notificationCount === undefined
        ? {}
        : { notifications: { unreadCount: notificationCount } })}
      {...(canSearch && onSearch !== undefined ? { onSearch } : {})}
      {...(accountItems.length > 0 ? { accountItems } : {})}
      {...(pageDescription === undefined ? {} : { pageDescription })}
      {...(pageTitle === undefined ? {} : { pageTitle })}
      /*
       * NO HELP CONTROL, deliberately, and the shell supports one.
       *
       * The approved reference shows a "?" beside the notifications bell. There
       * is no help route, no documentation site and no support surface in this
       * product, so wiring it would put a third control in the header that
       * opens nothing — the same defect as the count nobody counted and the ⌘K
       * badge that did nothing, both of which P1 removed. `onHelp` exists on
       * `AppShell` and the control appears the day there is somewhere for it
       * to go.
       */
    >
      {children}
    </AppShell>
  );
}
